/*
  Carousel with lightbox product gallery.
  <product-carousel-gallery id="MediaGallery-{section}"> exposes
  setActiveMedia(mediaId, prepend, scrollIntoView) so the theme's variant
  code in global.js keeps working. <carousel-slider> wraps the track and
  exposes goToFirstSlide(), which global.js calls after prepending the
  variant's featured media.
*/

if (!customElements.get('carousel-slider')) {
  customElements.define(
    'carousel-slider',
    class CarouselSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.pgc__track');
        if (!this.track) return;
        this.currentIndex = 0;
        this.scrollTimer = null;
        this.track.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.onScrollSettled(), 80);
        });
      }

      // All slides in DOM order, including ones hidden by the
      // "hide other variants' media" CSS rule (.pgc__slide--variant).
      get trackChildren() {
        return this.track ? Array.from(this.track.children) : [];
      }

      // Only the currently visible slides. Navigation, the counter and the
      // progress bar all operate on this list so hidden variant media never
      // counts toward position/total.
      get slides() {
        return this.trackChildren.filter((el) => el.offsetParent !== null);
      }

      onScrollSettled() {
        const width = this.track.clientWidth;
        if (!width) return;
        const index = Math.min(
          Math.max(Math.round(this.track.scrollLeft / width), 0),
          this.slides.length - 1
        );
        this.setIndex(index);
      }

      setIndex(index) {
        if (index === this.currentIndex) return;
        this.currentIndex = index;
        this.dispatchEvent(
          new CustomEvent('pgc:change', { bubbles: true, detail: { index, currentElement: this.slides[index] } })
        );
      }

      goTo(index, behavior = 'smooth') {
        const slides = this.slides;
        if (!slides.length) return;
        const clamped = Math.min(Math.max(index, 0), slides.length - 1);
        this.track.scrollTo({ left: slides[clamped].offsetLeft, behavior });
        this.setIndex(clamped);
      }

      goToFirstSlide() {
        this.goTo(0, 'auto');
      }
    }
  );
}

if (!customElements.get('product-carousel-gallery')) {
  customElements.define(
    'product-carousel-gallery',
    class ProductCarouselGallery extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('carousel-slider');
        this.thumbList = this.querySelector('.pgc__thumbnails');
        this.counter = this.querySelector('[data-pgc-current]');
        this.counterTotal = this.querySelector('[data-pgc-total]');
        this.progressBar = this.querySelector('[data-pgc-progress]');
        this.prevButton = this.querySelector('.pgc__nav--prev');
        this.nextButton = this.querySelector('.pgc__nav--next');
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        if (!this.slider) return;

        this.addEventListener('pgc:change', (event) => this.updateUI(event.detail.index));

        if (this.thumbList) {
          this.thumbList.addEventListener('click', (event) => {
            const item = event.target.closest('[data-target]');
            if (!item) return;
            this.slider.goTo(this.indexOfMedia(item.dataset.target));
          });
        }

        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.slider.goTo(this.slider.currentIndex - 1));
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.slider.goTo(this.slider.currentIndex + 1));
        }

        if (this.thumbList && 'ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(([entry]) => {
            this.style.setProperty('--pgc-thumbs-max-height', `${entry.contentRect.height}px`);
          });
          this.resizeObserver.observe(this.slider);
        }

        this.initLightbox();

        // Sync counter/progress/nav to the real DOM state on load, without
        // announcing to screen readers (nothing has "changed" yet).
        this.updateUI(0, { announce: false });
      }

      disconnectedCallback() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      indexOfMedia(mediaId) {
        return this.slider.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      }

      // Called by global.js's updateGangMedia() for a variant that has no
      // featured_media of its own (only "gang" alt-tagged group media, e.g.
      // #fabric-color_bisque). Gang toggling has already moved the right
      // slide(s) to the front and set .gang__active by the time this runs -
      // this just scrolls back to the front and refreshes the counter,
      // progress bar, thumbnail highlight and nav arrows to match.
      refreshActiveSlide() {
        if (!this.slider) return;
        this.slider.goToFirstSlide();
        this.updateUI(0);
      }

      async initLightbox() {
        const lightboxUrl = this.dataset.photoswipeLightboxUrl;
        const moduleUrl = this.dataset.photoswipeModuleUrl;
        if (!lightboxUrl || !moduleUrl) return;
        try {
          const { default: PhotoSwipeLightbox } = await import(lightboxUrl);
          this.lightbox = new PhotoSwipeLightbox({
            gallery: this.slider,
            children: 'a.pgc__zoom-link',
            pswpModule: () => import(moduleUrl),
            bgOpacity: 1,
            wheelToZoom: true,
            mainClass: 'pswp--pgc',
          });
          this.lightbox.on('change', () => {
            const element = this.lightbox.pswp?.currSlide?.data?.element;
            if (!element) return;
            const slide = element.closest('.pgc__slide');
            if (!slide) return;
            const index = this.slider.slides.indexOf(slide);
            if (index > -1 && index !== this.slider.currentIndex) this.slider.goTo(index, 'auto');
          });
          this.lightbox.init();
        } catch (error) {
          // Lightbox failed to load; zoom links fall back to opening the image in a new tab.
          console.warn('Product gallery lightbox unavailable', error);
        }
      }

      setActiveMedia(mediaId, prepend, scrollIntoView = true) {
        // Search all slides, not just visible ones: the slide being
        // activated may currently be hidden because it belongs to a
        // variant that isn't selected yet (see .pgc__slide--variant).
        const slide = this.slider.trackChildren.find((el) => el.dataset.mediaId === mediaId);
        if (!slide) return;

        if (prepend && slide !== this.slider.track.firstElementChild) {
          this.slider.track.prepend(slide);
        }
        this.syncThumbnailOrder();

        const index = this.slider.slides.indexOf(slide);
        this.slider.goTo(Math.max(index, 0), scrollIntoView ? 'smooth' : 'auto');
        this.updateUI(Math.max(index, 0));
      }

      // Mirrors the track's full DOM order (including hidden variant
      // slides) onto the thumbnail list, so whichever slide is prepended
      // to the front becomes the thumbnail list's first child too - that's
      // what the .pgc__thumbnail-item--variant:not(:first-child) rule
      // keys off of.
      syncThumbnailOrder() {
        if (!this.thumbList) return;
        this.slider.trackChildren.forEach((slide) => {
          const item = this.thumbList.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
          if (item) this.thumbList.appendChild(item);
        });
      }

      updateUI(index, { announce = true } = {}) {
        const slides = this.slider.slides;
        const total = slides.length;
        const activeSlide = slides[index];
        if (!activeSlide) return;

        if (this.thumbList) {
          this.thumbList.querySelectorAll('.pgc__thumbnail').forEach((button) => button.removeAttribute('aria-current'));
          const activeThumb = this.thumbList.querySelector(`[data-target="${activeSlide.dataset.mediaId}"] .pgc__thumbnail`);
          if (activeThumb) {
            activeThumb.setAttribute('aria-current', 'true');
            activeThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }

        if (this.counter) this.counter.textContent = index + 1;
        if (this.counterTotal) this.counterTotal.textContent = total;
        if (this.progressBar) {
          this.progressBar.style.width = `${100 / total}%`;
          this.progressBar.style.transform = `translateX(${index * 100}%)`;
        }
        if (this.prevButton) this.prevButton.hidden = index === 0;
        if (this.nextButton) this.nextButton.hidden = index === total - 1;

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        if (announce && this.liveRegion && window.theme?.accessibilityStrings?.imageAvailable) {
          this.liveRegion.setAttribute('aria-hidden', 'false');
          this.liveRegion.innerHTML = window.theme.accessibilityStrings.imageAvailable.replace(
            '[index]',
            activeSlide.dataset.mediaPosition || index + 1
          );
          clearTimeout(this.liveRegionTimer);
          this.liveRegionTimer = setTimeout(() => this.liveRegion.setAttribute('aria-hidden', 'true'), 2000);
        }
      }
    }
  );
}
